/**
 * The rules of the shared tData tracking cookie, as pure functions (no window, document or
 * cookies here, so they can be tested). Keep IDENTICAL to React-Membership's
 * helpers/trackingRules.js: both apps read and write the same cookie (Signup/docs/41).
 *
 * The cookie holds three sets, each with its own clock:
 * - the last touch: the campaign of the latest visit that carried one (queryParamSource,
 *   utm*, ad click ids, queryParams), kept LAST_TOUCH_DAYS after lastTouchAt;
 * - the first touch: referrer and landing page of the first visit, kept FIRST_TOUCH_DAYS
 *   after firstTouchAt;
 * - the internal trail: internalSource, kept 1 day after internalSourceAt.
 * Rewriting the cookie never restarts a clock; only a new visit's own data does.
 */

export const LAST_TOUCH_DAYS = 7;
export const FIRST_TOUCH_DAYS = 30;
// The cookie outlives its longest set; each set expires on its own clock inside it.
export const COOKIE_EXPIRY_DAYS = FIRST_TOUCH_DAYS;

const DAY_MS = 24 * 60 * 60 * 1000;
const INTERNAL_SOURCE_MAX_AGE_MS = DAY_MS;

// Ad platform click ids: Google (web, iOS app and web-to-app), Meta, Microsoft, TikTok.
export const CLICK_ID_PARAMS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid", "ttclid"];

// Query-string params parsed into named fields.
export const MARKETING_PARAM_MAP: { [param: string]: string } = {
  utm_source: "utmSource",
  utm_medium: "utmMedium",
  utm_campaign: "utmCampaign",
  utm_term: "utmTerm",
  utm_content: "utmContent",
  gclid: "gclid",
  gbraid: "gbraid",
  wbraid: "wbraid",
  fbclid: "fbclid",
  msclkid: "msclkid",
  ttclid: "ttclid",
};

// The campaign itself: src, the utm tags and the click ids, as named fields.
const CAMPAIGN_FIELDS = ["queryParamSource"].concat(
  Object.keys(MARKETING_PARAM_MAP).map((param) => MARKETING_PARAM_MAP[param])
);
// The last touch: replaced together, expired together.
const LAST_TOUCH_FIELDS = ["queryParams"].concat(CAMPAIGN_FIELDS);
const FIRST_TOUCH_FIELDS = ["referrer", "landingPage"];

// Functional or sensitive params never stored: k/tk are sign-in keys, eref a signed email
// reference (Signup/docs/34), t an unsubscribe token, login and email a person's address (the
// login hand-off brings login=, Signup/docs/42 D1).
export const QUERY_PARAM_BLACKLIST = [
  "join", "code", "promo", "page", "redirect", "homekit",
  "address", "redirectUrl", "product", "returnUrl",
  "k", "tk", "eref", "t", "login", "email",
];

// Our own sites, and Stripe's pages people come back from: never a first referrer.
const OWN_HOST_SUFFIXES = [
  "castingworkbook.com", "thecwbint.com", "thetwbint.com", "thecwb.com", "thetwb.com",
  "theworkbook.com", "stripe.com",
];

// No referrer at all on the first visit: typed, bookmarked, or an app that sends none.
export const DIRECT_REFERRER = "(direct)";

const hasValue = (value: any): boolean => value !== undefined && value !== null && value !== "";

const ageMs = (iso: any, now: number): number => {
  const at = Date.parse(iso);
  return isNaN(at) ? Infinity : now - at;
};

/** The query string without blacklisted params, '?'-prefixed, or "" when nothing is left. */
export const filterQueryStrings = (queryString: string): string => {
  if (!queryString) return queryString;
  try {
    const params = new URLSearchParams(queryString);
    QUERY_PARAM_BLACKLIST.forEach((key) => params.delete(key));
    const filtered = params.toString();
    return filtered ? `?${filtered}` : "";
  } catch (e) {
    return "";
  }
};

export const isCampaignParam = (key: string): boolean => {
  const name = String(key || "").toLowerCase();
  return name === "src" || name.indexOf("utm_") === 0 || CLICK_ID_PARAMS.indexOf(name) !== -1;
};

/** True when the URL brings a campaign: src, any utm_*, or an ad click id, with a value. */
export const hasCampaignSignal = (search: string): boolean => {
  try {
    if (!search) return false;
    let found = false;
    new URLSearchParams(search).forEach((value, key) => {
      if (value && isCampaignParam(key)) found = true;
    });
    return found;
  } catch (e) {
    return false;
  }
};

/**
 * Every campaign pair of a query string (src, all utm_*, the click ids; names in any case), in
 * a fixed order: two visits with the same pairs are the same campaign, an unnamed tag
 * (utm_id, utm_source_platform…) included.
 */
const campaignPairs = (queryString: string): string => {
  try {
    const pairs: string[] = [];
    new URLSearchParams(queryString || "").forEach((value, key) => {
      if (value && isCampaignParam(key)) pairs.push(`${String(key).toLowerCase()}=${value}`);
    });
    return pairs.sort().join("&");
  } catch (e) {
    return "";
  }
};

/** The same contents whatever the key order: a rewrite that only moves keys changes nothing. */
export const sameData = (a: any, b: any): boolean => {
  if (!a || !b || typeof a !== "object" || typeof b !== "object") return a === b;
  const keysOf = (o: any) => Object.keys(o).filter((key) => o[key] !== undefined).sort();
  const keysA = keysOf(a);
  const keysB = keysOf(b);
  return keysA.length === keysB.length && keysA.every((key, i) => key === keysB[i] && a[key] === b[key]);
};

/** The URL's last-touch fields: src, the named campaign fields and the filtered query string. */
export const parseVisit = (search: string): any => {
  const visit: any = {};
  try {
    // Names in any case, as hasCampaignSignal reads them (?UTM_Campaign= is a campaign too);
    // the first value of a name wins.
    new URLSearchParams(search || "").forEach((value, key) => {
      if (!value) return;
      const name = String(key || "").toLowerCase();
      const field = name === "src" ? "queryParamSource"
        : Object.prototype.hasOwnProperty.call(MARKETING_PARAM_MAP, name) ? MARKETING_PARAM_MAP[name] : null;
      if (field && !visit[field]) visit[field] = value;
    });
    const filtered = filterQueryStrings(search);
    if (filtered) visit.queryParams = filtered;
  } catch (e) { /* tracking is best-effort */ }
  return visit;
};

const isOwnHost = (host: string, currentHost: string): boolean => {
  const name = String(host || "").toLowerCase();
  if (!name) return false;
  if (name === String(currentHost || "").toLowerCase() || name === "localhost" || name === "127.0.0.1") return true;
  return OWN_HOST_SUFFIXES.some((suffix) => name === suffix || name.endsWith(`.${suffix}`));
};

/**
 * The first visit's referrer as stored: origin + path for a web page (no query string),
 * scheme + host for an app (android-app://com.google.android.gm), DIRECT_REFERRER when there
 * is none, and undefined when it is one of our own sites (or Stripe), which says nothing
 * about how the person found us.
 */
export const classifyReferrer = (referrer: string, currentHost: string): string | undefined => {
  if (!referrer) return DIRECT_REFERRER;
  try {
    const url = new URL(referrer);
    if (url.protocol === "http:" || url.protocol === "https:") {
      if (isOwnHost(url.hostname, currentHost)) return undefined;
      return `${url.origin}${url.pathname}`;
    }
    if (url.host) return `${url.protocol}//${url.host}`;
    const app = referrer.split(/[/?#]/).filter(Boolean);
    return app.length > 1 ? `${url.protocol}//${app[1]}` : undefined;
  } catch (e) {
    return undefined;
  }
};

/**
 * A stored cookie as of now: each set past its time is dropped, and a set written before
 * the clocks existed gets one from now (so it expires on the same rules from here on).
 */
export const normalize = (stored: any, now: number): any => {
  if (!stored || typeof stored !== "object") return null;
  const data = { ...stored };
  const nowIso = new Date(now).toISOString();

  const hasLastTouch = LAST_TOUCH_FIELDS.some((field) => hasValue(data[field]));
  if (hasLastTouch && !data.lastTouchAt) data.lastTouchAt = nowIso;
  if (data.lastTouchAt && (!hasLastTouch || ageMs(data.lastTouchAt, now) > LAST_TOUCH_DAYS * DAY_MS)) {
    LAST_TOUCH_FIELDS.forEach((field) => delete data[field]);
    delete data.lastTouchAt;
  }

  const hasFirstTouch = FIRST_TOUCH_FIELDS.some((field) => hasValue(data[field]));
  if (hasFirstTouch && !data.firstTouchAt) data.firstTouchAt = nowIso;
  if (data.firstTouchAt && ageMs(data.firstTouchAt, now) > FIRST_TOUCH_DAYS * DAY_MS) {
    FIRST_TOUCH_FIELDS.forEach((field) => delete data[field]);
    delete data.firstTouchAt;
  }

  if (data.internalSource && ageMs(data.internalSourceAt, now) > INTERNAL_SOURCE_MAX_AGE_MS) {
    delete data.internalSource;
    delete data.internalSourceAt;
  }
  if (!data.internalSource) delete data.internalSourceAt;

  return data;
};

/**
 * The stored data with this URL's last touch applied (no first-touch capture).
 * A URL with a campaign replaces the whole last touch, query string included: one visit's
 * gclid never sits beside another visit's utm_campaign. A URL without one only adds its
 * params (agent invitation, labs flag) to the stored query string, and restarts no clock.
 */
export const applyLastTouch = (data: any, search: string, now: number): any => {
  const result = { ...(data || {}) };
  const visit = parseVisit(search);

  if (hasCampaignSignal(search)) {
    // The same campaign again (a reload, a restored tab, a route mount) keeps its clock: only a
    // different one restarts it, and a new ad click always is (each has its own click id).
    // Compared on every campaign pair of the query string, not only the named fields.
    const sameCampaign = !!result.lastTouchAt &&
      campaignPairs(result.queryParams) === campaignPairs(visit.queryParams);
    const at = sameCampaign ? result.lastTouchAt : new Date(now).toISOString();
    LAST_TOUCH_FIELDS.forEach((field) => delete result[field]);
    Object.keys(visit).forEach((field) => { result[field] = visit[field]; });
    result.lastTouchAt = at;
    return result;
  }

  if (visit.queryParams) {
    try {
      const merged = new URLSearchParams(visit.queryParams);
      new URLSearchParams(result.queryParams || "").forEach((value, key) => {
        if (!merged.get(key)) merged.set(key, value);
      });
      result.queryParams = `?${merged.toString()}`;
    } catch (e) {
      result.queryParams = visit.queryParams;
    }
    if (!result.lastTouchAt) result.lastTouchAt = new Date(now).toISOString();
  }
  return result;
};

/**
 * The cookie after a page visit: expired sets dropped, this URL's last touch applied, and
 * the first touch captured when none is held. The first touch is written once per
 * FIRST_TOUCH_DAYS: the landing page and the referrer as classifyReferrer stores it.
 */
export const applyVisit = (
  stored: any,
  visit: { search: string; referrer: string; landingPage: string; host: string },
  now: number
): any => {
  const result = applyLastTouch(normalize(stored, now), visit.search, now);
  if (!result.firstTouchAt) {
    FIRST_TOUCH_FIELDS.forEach((field) => delete result[field]);
    const referrer = classifyReferrer(visit.referrer, visit.host);
    if (referrer) result.referrer = referrer;
    if (visit.landingPage) result.landingPage = visit.landingPage;
    result.firstTouchAt = new Date(now).toISOString();
  }
  return result;
};
